import { useEffect } from "react";

interface IIntersectionObserverParams {
	listRef: React.RefObject<HTMLDivElement>;
	sectionRefs: React.MutableRefObject<Map<string, HTMLDivElement | null>>;
	isClickScrolling: React.RefObject<boolean>;
	onScrollUpdateCategory: (categoryId: string) => void;
	bannerHeight: number;
	categoryListHeight: number;
	categories: Array<{ id: string }>;
}

// Smallest usable trigger zone (px) between the top and bottom exclusions.
const MIN_TRIGGER_ZONE = 72;

export const useIntersectionObserver = ({
	listRef,
	sectionRefs,
	isClickScrolling,
	onScrollUpdateCategory,
	bannerHeight,
	categoryListHeight,
	categories,
}: IIntersectionObserverParams) => {
	useEffect(() => {
		const scrollRoot = listRef.current;

		if (!scrollRoot) return;

		const observerCallback: IntersectionObserverCallback = (entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting && !isClickScrolling.current) {
					const categoryId = (entry.target as HTMLElement).id.replace("category-", "");

					onScrollUpdateCategory(categoryId);
				}
			}
		};

		let observer: IntersectionObserver | null = null;
		let observedRootHeight = 0;

		const observeSections = () => {
			observer?.disconnect();
			observedRootHeight = scrollRoot.clientHeight;

			// Dynamically calculate rootMargin to exclude Banner and CategoryList.
			// Bottom margin uses px (60% of root height at observe time) instead of a
			// percentage so Android overscroll does not drift the trigger zone. On a
			// wide, short root (foldable inner screen in landscape) the two exclusions
			// would overlap and no section could ever trigger, so the bottom margin is
			// capped to leave at least MIN_TRIGGER_ZONE; portrait phones are untouched.
			const topMargin = bannerHeight + categoryListHeight;
			const bottomMargin = Math.max(
				0,
				Math.min(
					Math.floor(observedRootHeight * 0.6),
					observedRootHeight - topMargin - MIN_TRIGGER_ZONE
				)
			);
			const observerOptions: IntersectionObserverInit = {
				root: scrollRoot,
				rootMargin: `-${topMargin}px 0px -${bottomMargin}px 0px`,
				threshold: [0, 0.1],
			};

			observer = new IntersectionObserver(observerCallback, observerOptions);

			// Observe all existing sections
			for (const category of categories) {
				const element = sectionRefs.current.get(category.id);

				if (element) {
					observer.observe(element);
				}
			}
		};

		observeSections();

		// The bottom margin is frozen in px at observe time, so a scroll root that
		// changes height without a route change (fold / unfold, rotation, address
		// bar) would keep a trigger zone sized for the old height. Re-observe when
		// the root's height changes.
		const resizeObserver =
			typeof ResizeObserver === "undefined"
				? null
				: new ResizeObserver(() => {
						if (scrollRoot.clientHeight !== observedRootHeight) {
							observeSections();
						}
					});

		resizeObserver?.observe(scrollRoot);

		return () => {
			resizeObserver?.disconnect();
			observer?.disconnect();
		};
	}, [
		listRef,
		sectionRefs,
		isClickScrolling,
		onScrollUpdateCategory,
		bannerHeight,
		categoryListHeight,
		categories,
	]);
};
